import { create } from 'zustand'
import type { ResearchProgress } from '@/features/research/use-research'
import type { WikiState } from '@/features/wiki/use-wiki'
import type { OutputState } from '@/features/output/use-output'
import type { ImageState } from '@/features/image/use-image'

/**
 * Globaler Run-Store fuer Research-/Wiki-/Output-/Image-Runs (M2-B).
 * Gleiches Muster wie streaming-store.ts: State + SSE-Treiber haengen nicht
 * an Komponenten, deshalb ueberlebt ein gestarteter Run das Wegnavigieren.
 * Beim Zurueckkehren sieht der User den laufenden oder finalen Stand; der
 * Assistant-Stub wird (wie bisher ueber die Chat-Page-Watcher) persistiert —
 * lastStubId im Slice verhindert Doppel-Persistierung ueber Mounts hinweg.
 *
 * Hinweis: import type oben ist bewusst type-only, damit kein Runtime-Zyklus
 * zwischen Hooks und diesem Store entsteht.
 */

export type RunKind = 'research' | 'wiki' | 'output' | 'image'

export interface RunStateMap {
  research: ResearchProgress | null
  wiki: WikiState
  output: OutputState
  image: ImageState
}

export interface RunSlice<T> {
  state: T
  /** Chat-Session, an die der Run gebunden ist (Stub-Ziel + Sichtbarkeit). */
  sessionId: string | null
  /** Dedup: fuer diese Result-/Fehler-Id wurde bereits ein Assistant-Stub persistiert. */
  lastStubId: string | null
}

type RunSlices = { [K in RunKind]: RunSlice<RunStateMap[K]> }

const wikiInitial: WikiState = { operation: 'create', status: 'idle', pages: [], isRunning: false }
const outputInitial: OutputState = { status: 'idle', streamingContent: '', isRunning: false }
const imageInitial: ImageState = { status: 'idle', isRunning: false }

function initialSlices(): RunSlices {
  return {
    research: { state: null, sessionId: null, lastStubId: null },
    wiki: { state: wikiInitial, sessionId: null, lastStubId: null },
    output: { state: outputInitial, sessionId: null, lastStubId: null },
    image: { state: imageInitial, sessionId: null, lastStubId: null },
  }
}

export const useRunStore = create<RunSlices>(() => initialSlices())

// AbortController leben ausserhalb von Komponenten-Refs, damit cancel/reset
// auch nach einem Remount noch den laufenden Fetch erreichen.
const abortControllers: Partial<Record<RunKind, AbortController>> = {}

export function setRunAbortController(kind: RunKind, controller: AbortController): void {
  abortControllers[kind] = controller
}

export function isRunController(kind: RunKind, controller: AbortController): boolean {
  return abortControllers[kind] === controller
}

export function abortRun(kind: RunKind): void {
  abortControllers[kind]?.abort()
  delete abortControllers[kind]
}

/** Neuen Run starten: frischer State, Session-Bindung, Stub-Dedup zurueck. */
export function beginRun<K extends RunKind>(kind: K, state: RunStateMap[K], sessionId: string | null): void {
  useRunStore.setState(
    (s) => ({ ...s, [kind]: { state, sessionId, lastStubId: null } }) as RunSlices,
  )
}

/** Funktionaler State-Update (wie setState) fuer laufende SSE-Events. */
export function updateRunState<K extends RunKind>(
  kind: K,
  updater: (prev: RunStateMap[K]) => RunStateMap[K],
): void {
  useRunStore.setState((s) => {
    const slice = s[kind]
    return { ...s, [kind]: { ...slice, state: updater(slice.state) } } as RunSlices
  })
}

/**
 * Markiert einen Assistant-Stub als persistiert. Ueber getState() gelesen ist
 * die Markierung sofort synchron sichtbar — sicher gegen StrictMode-Doppel-
 * Effekte und Mehrfach-Persistierung ueber Mounts hinweg.
 */
export function markRunStubPersisted(kind: RunKind, stubId: string): void {
  useRunStore.setState((s) => {
    const slice = s[kind]
    return { ...s, [kind]: { ...slice, lastStubId: stubId } } as RunSlices
  })
}

/** Run auf Initial-State zuruecksetzen (Dismiss). Bricht NICHT ab — dafuer abortRun. */
export function clearRun(kind: RunKind): void {
  useRunStore.setState((s) => ({ ...s, [kind]: initialSlices()[kind] }) as RunSlices)
}
